import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, DatePicker, InputNumber, Segmented, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import UserAvatar from 'components/user-avatar';
import { useCurrentRoles } from 'hooks/use-current-roles';

interface RankingRow {
  id: number;
  rank: number;
  tiktokIdol: string;
  tiktokUsername?: string;
  agencyName?: string;
  totalDiamond: string;
  hour: number;
  day: number;
  asOf: string;
  user?: { nickName?: string; username?: string; profile?: { avatar?: string } };
}

interface MonthArchive {
  id: number;
  fileName: string;
  rows: number;
}

const currentMonth = () => dayjs().format('YYYY-MM');

// Trang chỉ XEM: file TikTok nhập ở Doanh thu → Nhập dữ liệu (/data-import).
export default function CreatorRanking() {
  const { canImportData } = useCurrentRoles();
  const [mode, setMode] = useState<'daily' | 'monthly'>('daily');
  const [selected, setSelected] = useState(dayjs().subtract(1, 'month'));
  const [limit, setLimit] = useState(20);
  const [rows, setRows] = useState<RankingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [archives, setArchives] = useState<MonthArchive[]>([]);
  const month = mode === 'daily' ? currentMonth() : selected.format('YYYY-MM');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    AxiosUtils.get(`${API.revenues}/top`, { date: `${month}-01`, type: 'CREATOR', limit })
      .then((res: any) => { if (active) setRows(res?.data || []); })
      .catch(() => { if (active) { setError(true); setRows([]); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month, limit]);

  useEffect(() => {
    if (mode !== 'monthly') { setArchives([]); return; }
    let active = true;
    AxiosUtils.get('/users/tiktok-import/archive', { month })
      .then((res: any) => { if (active) setArchives(res?.data || []); })
      .catch(() => { if (active) setArchives([]); });
    return () => { active = false; };
  }, [month, mode]);

  const download = async (archive: MonthArchive) => {
    try {
      const res: any = await AxiosUtils.get(`/users/tiktok-import/archive/${archive.id}/download`, undefined, undefined, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = archive.fileName;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError(true);
    }
  };

  return (
    <Card title="Data Kim cương · BXH Creator" style={{ marginBottom: 16 }}>
      <Space wrap size={12} style={{ marginBottom: 12 }}>
        <Segmented
          value={mode}
          onChange={(value) => setMode(value as 'daily' | 'monthly')}
          options={[{ label: 'Tháng hiện tại', value: 'daily' }, { label: 'Tháng đã chốt', value: 'monthly' }]}
        />
        {mode === 'monthly' && (
          <DatePicker
            picker="month"
            value={selected}
            format="MM/YYYY"
            allowClear={false}
            disabledDate={(date) => date.isAfter(dayjs(), 'month')}
            onChange={(date) => { if (date) setSelected(date); }}
          />
        )}
        <Typography.Text>Top</Typography.Text>
        <InputNumber min={1} max={100} value={limit} onChange={(value) => setLimit(value || 20)} style={{ width: 72 }} />
        {canImportData && (
          // Liên kết thường, không bọc Button trong thẻ a (hai phần tử bấm được lồng nhau).
          <Link to={mode === 'daily' ? '/data-import' : '/data-import?tab=thang'}>
            <UploadOutlined /> Nhập dữ liệu
          </Link>
        )}
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {mode === 'daily'
          ? 'Tháng hiện tại: số liệu là tổng các ngày đã nhập.'
          : 'Tháng đã chốt: số liệu theo file tháng đã nhập.'}
        {' '}Bảng xếp hạng trong app mặc định hiện Top 20.
      </Typography.Paragraph>
      {mode === 'monthly' && !!archives.length && (
        <div style={{ marginBottom: 12 }}>
          <Typography.Text strong>File gốc đã lưu (mọi cột): </Typography.Text>
          {archives.map((archive) => (
            <Button key={archive.id} type="link" onClick={() => download(archive)}>
              {archive.fileName} · {archive.rows} dòng
            </Button>
          ))}
        </div>
      )}
      {error && <Alert type="error" showIcon message="Chưa tải được bảng xếp hạng. Vui lòng thử lại." style={{ marginBottom: 12 }} />}
      <Typography.Text strong>{`Tháng ${month.slice(5)}/${month.slice(0, 4)}`}</Typography.Text>
      {rows[0]?.asOf && (
        <Typography.Text type="secondary">{` · Dữ liệu đến ngày ${dayjs(rows.reduce((latest, row) => row.asOf > latest ? row.asOf : latest, '')).format('DD/MM/YYYY')}`}</Typography.Text>
      )}
      <Table
        style={{ marginTop: 12 }}
        rowKey={(row) => `${row.id}-${row.rank}`}
        loading={loading}
        size="small"
        dataSource={rows}
        pagination={false}
        scroll={{ x: 680 }}
        locale={{ emptyText: 'Tháng này chưa được nhập dữ liệu TikTok' }}
        columns={[
          { title: '#', dataIndex: 'rank', width: 50 },
          {
            title: 'Creator · TikTok ID', key: 'creator',
            render: (_, row) => {
              return <Space><UserAvatar src={row.user?.profile?.avatar} /><span>{row.user?.nickName || row.tiktokUsername || row.tiktokIdol}<br /><Typography.Text type="secondary">@{row.tiktokUsername || row.tiktokIdol}</Typography.Text></span></Space>;
            },
          },
          { title: 'Đối tác', dataIndex: 'agencyName', render: (v) => v || '—' },
          { title: 'Kim cương', dataIndex: 'totalDiamond', align: 'right', render: (v) => Number(v).toLocaleString('vi-VN') },
          { title: 'LIVE', key: 'live', render: (_, row) => `${row.day} ngày · ${row.hour} giờ` },
        ]}
      />
    </Card>
  );
}
